import React from 'react';
import {useNavigate} from 'react-router-dom';
import Icon from '@/shared/components/Icon';
import {useTestsState} from '@/modules/patient-dashboard/hooks/useTestsQuery';
import {useDashboardData} from '@/modules/patient-dashboard/hooks/useDashboardData';
import {useAuth} from '@/modules/auth/hooks/useAuth';
import {
    getLatestBiomarker,
    formatTestDate,
    parseReferenceRange,
    calculateTrendChange,
    cleanBiomarkerName,
    getAllUniqueBiomarkers,
    getBiomarkerByName,
    parseNumericValue,
} from '@/shared/utils/biomarkerUtils';
import {resolveDisplayRule} from '@/shared/biomarkers/resolveDisplayRule';
import {ruleToConfig, valueOnlyReferenceText} from '@/shared/biomarkers/ruleToConfig';
import {useDisplayRules} from '@/shared/hooks/useDisplayRules';
import {BiomarkerStatus} from '@/shared/types/biomarker';
import SectionContainer from '@/shared/components/SectionContainer';
import BiomarkerSelect from '@/shared/components/BiomarkerSelect';
import ProgressBar from '@/shared/charts/ProgressBar';
import TestingHistory, {DESIGN_LEAD} from '@/shared/charts/TestingHistory';
import {getTestEducation} from '@/shared/data/testEducation';
import {BIOMARKER_STATUS_COLORS, BIOMARKER_VALUE_COLOR} from '@/shared/constants/colors';
import {useBiomarkerZones} from '@/shared/hooks/useBiomarkerZones';
import {getTestCategory} from '@/modules/patient-dashboard/utils/healthCategories';
import {buildTestingHistoryChartData, getHistoricalData} from '@/shared/utils/resultUtils';
import SectionHeader from '@/shared/components/SectionHeader';
import Card from '@/shared/components/Card';
import clsx from 'clsx';

/**
 * Helper function to format text with bold titles (lines ending with ':')
 */
const formatTextWithBoldTitles = (text: string): React.ReactNode => {
    const lines = text.split('\n');
    return lines.map((line, index) => {
        // Check if line ends with ':' (title line)
        const colonMatch = line.match(/^(.+:)(.*)$/);
        if (colonMatch) {
            const [, title, rest] = colonMatch;
            return (
                <React.Fragment key={index}>
                    <strong>{title}</strong>
                    {rest}
                    {index < lines.length - 1 && '\n'}
                </React.Fragment>
            );
        }
        return (
            <React.Fragment key={index}>
                {line}
                {index < lines.length - 1 && '\n'}
            </React.Fragment>
        );
    });
};

interface LabResultsProps {
    selectedBiomarkerName?: string | null;
    onBiomarkerChange?: (name: string | null) => void;
    onCategoryChange?: (category: string) => void;
}

const LabResults: React.FC<LabResultsProps> = ({selectedBiomarkerName, onBiomarkerChange, onCategoryChange}) => {
    const testsState = useTestsState();
    const {user} = useAuth();
    const {rules} = useDisplayRules();

    // Get gender from user info for gender-specific biomarker configs
    const gender: 'male' | 'female' | undefined =
        user?.userInfo?.gender === 'F' ? 'female' : user?.userInfo?.gender === 'M' ? 'male' : undefined;

    // Always the patient's real data — never gated by membership.
    const {data: tests} = useDashboardData();
    const navigate = useNavigate();
    const isLoading = testsState.loading;
    const isError = false; // Redux doesn't currently track errors

    // Get all unique biomarkers for the dropdown
    const allBiomarkers = React.useMemo(() => getAllUniqueBiomarkers(tests, gender, rules), [tests, gender, rules]);

    // State for selected biomarker - initialize with the latest biomarker
    const defaultBiomarker = React.useMemo(() => getLatestBiomarker(tests, gender, rules), [tests, gender, rules]);
    const [selectedBiomarker, setSelectedBiomarker] = React.useState(defaultBiomarker);

    // Biomarker Insights is collapsible. It defaults to expanded and re-opens
    // whenever a test is selected (dropdown, Lab Trends, or Health Overview);
    // the user can still collapse it manually via the header toggle.
    const [insightsOpen, setInsightsOpen] = React.useState(true);

    // Update selected biomarker when default changes (e.g., new data loaded)
    React.useEffect(() => {
        if (defaultBiomarker && !selectedBiomarker) {
            setSelectedBiomarker(defaultBiomarker);
        }
    }, [defaultBiomarker, selectedBiomarker]);

    // Sync with external selection from parent (e.g., from LabTrends click)
    // Use a ref to track the last synced name to prevent infinite loops
    const lastSyncedNameRef = React.useRef<string | null>(null);

    React.useEffect(() => {
        if (selectedBiomarkerName && tests.length > 0 && selectedBiomarkerName !== lastSyncedNameRef.current) {
            const biomarker = getBiomarkerByName(tests, selectedBiomarkerName, gender, rules);
            if (biomarker) {
                lastSyncedNameRef.current = selectedBiomarkerName;
                setSelectedBiomarker(biomarker);
                setInsightsOpen(true); // keep Insights expanded when a test is selected
            }
        }
    }, [selectedBiomarkerName, tests, gender, rules]);

    // Handler for internal dropdown selection
    const handleBiomarkerSelect = (biomarker: typeof selectedBiomarker) => {
        setSelectedBiomarker(biomarker);
        setInsightsOpen(true); // keep Insights expanded when a test is selected
        if (biomarker) {
            const cleanedName = cleanBiomarkerName(biomarker.testName);
            lastSyncedNameRef.current = cleanedName;
            onBiomarkerChange?.(cleanedName);

            // Also update the category to match the selected biomarker
            const category = getTestCategory(biomarker);
            if (category !== 'other') {
                onCategoryChange?.(category);
            }
        }
    };

    // Use selected biomarker for display
    const latestBiomarker = selectedBiomarker;

    // Navigate to the full lab report (single-result page) for the order that
    // contains the selected biomarker. ProcessedBiomarker carries no id of its
    // own, so we recover the parent order id from `tests` by matching test_name
    // — the same lookup BiomarkerDetailChart uses for its "Results" links.
    const handleFullReport = () => {
        if (!latestBiomarker || !Array.isArray(tests)) return;
        const targetName = cleanBiomarkerName(latestBiomarker.testName);
        const parentTest = tests.find((test: any) =>
            test.results?.some((r: any) => cleanBiomarkerName(r.test_name) === targetName)
        );
        if (parentTest) {
            navigate(`/result/${parentTest.id}`);
        }
    };

    // Helper to get status color class
    const getStatusColorClass = (status: BiomarkerStatus) => {
        if (status === 'Normal' || status === 'Optimal') return 'text-mld-status-normal';
        if (status === 'High') return 'text-mld-status-high';
        if (status === 'Low') return 'text-mld-status-low';
        if (status === 'Borderline') return 'text-mld-status-borderline';
        if (status === 'Elevated') return 'text-mld-status-elevated';
        return 'text-gray-500';
    };

    // Get test education information
    const testEducation = latestBiomarker ? getTestEducation(latestBiomarker.testName) : null;

    // Generate trend data using the same logic as ResultsView.
    // `currentValue` is null for a non-numeric result (e.g. "No Result"); the
    // trend builder then gaps that reading instead of plotting a 0.
    const trendData = React.useMemo(() => {
        if (!latestBiomarker) return [];
        const currentValue = parseNumericValue(latestBiomarker.result);

        const history = getHistoricalData(latestBiomarker.testName, currentValue, true, tests || [], true);
        const currentDate = latestBiomarker.statusDate || null;

        return buildTestingHistoryChartData({
            history,
            currentValue,
            currentDate,
            maxHistoryPoints: 3,
        });
    }, [latestBiomarker, tests]);

    // Check if this is a binary test (positive/negative) - handle typo 'Postive'
    const isBinaryTest =
        latestBiomarker?.result?.toLowerCase() === 'positive' ||
        latestBiomarker?.result?.toLowerCase() === 'postive' || // Handle typo
        latestBiomarker?.result?.toLowerCase() === 'negative' ||
        latestBiomarker?.status?.toLowerCase() === 'positive' ||
        latestBiomarker?.status?.toLowerCase() === 'postive' || // Handle typo
        latestBiomarker?.status?.toLowerCase() === 'negative';

    // Check if we should show testing history
    // For binary tests: always hide testing history
    // For other tests: show only if we have at least one real (non-null) point.
    // A lone non-numeric reading (current value null, no numeric history)
    // produces an all-null series — don't render an empty trend frame for it.
    const hasTestingHistory =
        !isBinaryTest && trendData && trendData.some((point) => point.value !== null && !point.dummy);

    // True when the current reading is a real number. Non-numeric results
    // (e.g. "No Result", "TNP") parse to null — for those we hide the slider
    // indicator dot since progressBarPosition falls back to a misleading 50.
    const hasNumericValue = latestBiomarker ? parseNumericValue(latestBiomarker.result) !== null : false;

    // Check for an admin-managed display rule (LOINC-first, falling back to
    // test name; sex-aware for HDL Direct/Uric Acid/etc.)
    const displayRule = latestBiomarker
        ? resolveDisplayRule(rules, {loinc: latestBiomarker.loinc, testName: latestBiomarker.testName, sex: gender})
        : null;
    const customConfig = displayRule ? ruleToConfig(displayRule) : null;
    const isValueOnly = valueOnlyReferenceText(displayRule) !== null;

    // Calculate reference range for thresholds
    const refRange = latestBiomarker?.refRange ? parseReferenceRange(latestBiomarker.refRange) : null;
    const thresholds = React.useMemo(
        () => (refRange ? {low: refRange.min, high: refRange.max} : {low: 0, high: 100}),
        [refRange]
    );

    const {progressBarSections, optimalZoneInfo} = useBiomarkerZones({
        rangeMin: refRange?.min ?? 0,
        rangeMax: refRange?.max ?? 100,
        rawRefRange: latestBiomarker?.refRange ?? '',
        customConfig,
    });

    // Show loading state
    if (isLoading) {
        return (
            <SectionContainer title="Lab Results">
                <div className="bg-white rounded-lg border border-mld-panel-border p-6 mb-4">
                    <div className="text-center py-8 text-mld-card-text">Loading latest lab results...</div>
                </div>
            </SectionContainer>
        );
    }

    // Show error state
    if (isError || !latestBiomarker) {
        return (
            <SectionContainer title="Lab Results">
                <div className="bg-white rounded-lg border border-mld-panel-border p-6 mb-4">
                    <div className="text-center py-8 text-mld-card-text">
                        {isError ? 'Unable to load lab results' : 'No lab results available'}
                    </div>
                </div>
            </SectionContainer>
        );
    }

    return (
        <section>
            <div className="flex justify-between items-end">
                <SectionHeader title="Lab Results" />
                <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 mb-2 text-sm text-mld-muted-text">
                        <Icon name="vial" className="h-5 w-5" />
                        <span>Select a Biomarker</span>
                    </div>
                    <BiomarkerSelect
                        biomarkers={allBiomarkers}
                        selectedBiomarker={selectedBiomarker}
                        onSelect={handleBiomarkerSelect}
                    />
                </div>
            </div>

            <Card variant="elevated" rounded="2xl" className="p-6">
                {/* Main content area */}
                <div className="p-4 mb-4">
                    <div
                        className={`grid gap-6 lg:gap-8 ${
                            hasTestingHistory ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'
                        }`}>
                        {/* Left side - Lab Result */}
                        <div className="relative">
                            {/* Header with title and value */}
                            <div className="flex justify-between items-start mb-2">
                                <div>
                                    <h3
                                        className="text-[24px] font-normal text-mld-card-text mb-0"
                                        style={{
                                            fontFamily: 'Poppins',
                                            fontWeight: 400,
                                            lineHeight: '1',
                                            letterSpacing: '0%',
                                        }}>
                                        {cleanBiomarkerName(latestBiomarker.testName)}
                                    </h3>
                                    {!isValueOnly &&
                                        latestBiomarker.status &&
                                        latestBiomarker.status.toLowerCase() !== 'unknown' &&
                                        (() => {
                                            const trendChange = calculateTrendChange(tests, latestBiomarker.testName);

                                            return (
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={`text-[20px] font-normal ${getStatusColorClass(
                                                            latestBiomarker.status
                                                        )}`}
                                                        style={{
                                                            fontFamily: 'Poppins',
                                                            fontWeight: 400,
                                                            lineHeight: 'auto',
                                                            letterSpacing: '0%',
                                                        }}>
                                                        {latestBiomarker.displayLabel}
                                                    </span>

                                                    {/* Status Icon */}
                                                    {latestBiomarker.status === 'Normal' ||
                                                    latestBiomarker.status === 'Optimal' ? (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-normal flex items-center justify-center">
                                                            <svg width="8" height="6" viewBox="0 0 8 6" fill="none">
                                                                <path
                                                                    d="M1 3L2.5 4.5L7 0.5"
                                                                    stroke={BIOMARKER_STATUS_COLORS.NORMAL}
                                                                    strokeWidth="1.2"
                                                                    strokeLinecap="round"
                                                                    strokeLinejoin="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    ) : latestBiomarker.status === 'High' ? (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-high flex items-center justify-center">
                                                            <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                                <path
                                                                    d="M1 0V6.5M1 8.5V9.5"
                                                                    stroke={BIOMARKER_STATUS_COLORS.HIGH}
                                                                    strokeWidth="1.5"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    ) : latestBiomarker.status === 'Low' ||
                                                      latestBiomarker.status === 'Elevated' ? (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-low flex items-center justify-center">
                                                            <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                                <path
                                                                    d="M1 0V6.5M1 8.5V9.5"
                                                                    stroke={BIOMARKER_STATUS_COLORS.LOW}
                                                                    strokeWidth="1.5"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    ) : latestBiomarker.status === 'Borderline' ? (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-mld-status-borderline flex items-center justify-center">
                                                            <svg width="2" height="10" viewBox="0 0 2 10" fill="none">
                                                                <path
                                                                    d="M1 0V6.5M1 8.5V9.5"
                                                                    stroke={BIOMARKER_STATUS_COLORS.BORDERLINE}
                                                                    strokeWidth="1.5"
                                                                    strokeLinecap="round"
                                                                />
                                                            </svg>
                                                        </div>
                                                    ) : (
                                                        <div className="w-4 h-4 rounded-full bg-white border-2 border-gray-500 flex items-center justify-center">
                                                            <svg width="2" height="2" viewBox="0 0 2 2" fill="none">
                                                                <circle cx="1" cy="1" r="0.8" fill="gray" />
                                                            </svg>
                                                        </div>
                                                    )}

                                                    {/* Trend Icon (new) */}
                                                    {trendChange.changeType === 'up' ? (
                                                        <Icon
                                                            name="chevron-up-solid"
                                                            className={`w-6 h-6 ${getStatusColorClass(
                                                                latestBiomarker.status
                                                            )}`}
                                                        />
                                                    ) : trendChange.changeType === 'down' ? (
                                                        <Icon
                                                            name="chevron-down-solid"
                                                            className={`w-6 h-6 ${getStatusColorClass(
                                                                latestBiomarker.status
                                                            )}`}
                                                        />
                                                    ) : (
                                                        <Icon
                                                            name="minus-solid"
                                                            className={`w-6 h-6 ${getStatusColorClass(
                                                                latestBiomarker.status
                                                            )}`}
                                                        />
                                                    )}
                                                </div>
                                            );
                                        })()}
                                </div>
                                <div className="text-right">
                                    <div
                                        className="text-[34px] font-normal"
                                        style={{
                                            fontFamily: 'Poppins',
                                            fontWeight: 400,
                                            lineHeight: '1',
                                            letterSpacing: '0%',
                                            color: BIOMARKER_VALUE_COLOR,
                                        }}>
                                        {latestBiomarker.result}
                                    </div>
                                    {hasNumericValue && latestBiomarker.units && (
                                        <div
                                            className="text-[22px] font-normal text-mld-card-text"
                                            style={{
                                                fontFamily: 'Poppins',
                                                fontWeight: 400,
                                                lineHeight: 'auto',
                                                letterSpacing: '0%',
                                                color: '#97A6AC',
                                            }}>
                                            {latestBiomarker.units}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Dynamic progress bar based on reference range */}
                            {!isValueOnly && (
                                <div className="mt-6 mb-14">
                                    {isBinaryTest ? (
                                        <ProgressBar
                                            sections={[
                                                {width: 50, color: BIOMARKER_STATUS_COLORS.NORMAL}, // Green for negative
                                                {width: 50, color: BIOMARKER_STATUS_COLORS.HIGH}, // Pink for positive
                                            ]}
                                            indicatorPosition={
                                                latestBiomarker.result?.toLowerCase() === 'positive' ||
                                                latestBiomarker.result?.toLowerCase() === 'postive' ||
                                                latestBiomarker.status?.toLowerCase() === 'positive' ||
                                                latestBiomarker.status?.toLowerCase() === 'postive'
                                                    ? 75
                                                    : 25
                                            }
                                            className="w-full mb-0"
                                        />
                                    ) : (
                                        <ProgressBar
                                            sections={progressBarSections}
                                            indicatorPosition={latestBiomarker.progressBarPosition}
                                            showIndicator={hasNumericValue}
                                            className="w-full mb-0"
                                        />
                                    )}

                                    {/* Range labels - positioned at edges of optimal/in-range zone */}
                                    {!isBinaryTest && (
                                        <div className="relative mt-6 text-xs text-gray-500">
                                            {/* Label positioned at center of optimal zone */}
                                            <div className="relative mb-3 flex">
                                                <span
                                                    className="absolute"
                                                    style={{
                                                        left: `${optimalZoneInfo.startPercent + (optimalZoneInfo.endPercent - optimalZoneInfo.startPercent) / 2}%`,
                                                        transform: 'translateX(-50%)',
                                                    }}>
                                                    {optimalZoneInfo.label}
                                                </span>
                                            </div>
                                            <div className="absolute top-0 left-0 w-full flex">
                                                {/* Min value with dashed line */}
                                                <div
                                                    className="absolute"
                                                    style={{
                                                        left: `calc(${optimalZoneInfo.startPercent}% + 8px)`,
                                                        transform: 'translateX(-50%)',
                                                    }}>
                                                    <div className="flex flex-col items-center">
                                                        <div
                                                            style={{
                                                                width: '2px',
                                                                height: '16px',
                                                                borderLeft: '2px dashed #9CA3AF',
                                                            }}
                                                        />
                                                        <span className="mt-2">{optimalZoneInfo.minValue}</span>
                                                    </div>
                                                </div>
                                                {/* Max value with dashed line */}
                                                <div
                                                    className="absolute"
                                                    style={{
                                                        left: `calc(${optimalZoneInfo.endPercent}% - 8px)`,
                                                        transform: 'translateX(-50%)',
                                                    }}>
                                                    <div className="flex flex-col items-center">
                                                        <div
                                                            style={{
                                                                width: '2px',
                                                                height: '16px',
                                                                borderLeft: '2px dashed #9CA3AF',
                                                            }}
                                                        />
                                                        <span className="mt-2">{optimalZoneInfo.maxValue}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Right side - Result History (only show if there's data) */}
                        {hasTestingHistory && (
                            <div className="lg:border-l border-mld-panel-border px-6 -mr-6 lg:-my-4">
                                <div className="flex items-baseline justify-between mb-2">
                                    <div className="flex items-center gap-1.5 text-mld-text-primary">
                                        <Icon name="chart-column" className="h-5 w-5" />
                                        <span className="text-base font-medium">Result History</span>
                                    </div>
                                    {/*<h3 className="text-base font-medium text-mld-text-primary">Result History</h3>*/}
                                    <p className="text-xs text-gray-500 inline-flex items-center gap-1">
                                        Last Tested {formatTestDate(latestBiomarker.statusDate)}
                                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" className="inline">
                                            <rect
                                                x="3"
                                                y="4"
                                                width="18"
                                                height="18"
                                                rx="2"
                                                ry="2"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            />
                                            <line x1="16" y1="2" x2="16" y2="6" stroke="currentColor" strokeWidth="2" />
                                            <line x1="8" y1="2" x2="8" y2="6" stroke="currentColor" strokeWidth="2" />
                                            <line
                                                x1="3"
                                                y1="10"
                                                x2="21"
                                                y2="10"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                            />
                                        </svg>
                                    </p>
                                </div>
                                {/* Framed chart — bordered rounded container matching the
                                    Health Overview mini-charts. The X axis is rendered
                                    OUTSIDE the frame (below) so the date labels sit under
                                    the chart like the design, with the current date boxed. */}
                                <div className="rounded-lg border border-mld-panel-border bg-white overflow-hidden">
                                    <TestingHistory
                                        data={trendData}
                                        title=""
                                        height={160}
                                        showGrid={false}
                                        showVerticalLines={false}
                                        thresholds={thresholds}
                                        showXAxis={false}
                                        navyDots={true}
                                    />
                                </div>
                                {/* Axis labels below the frame, absolutely positioned to
                                    line up with the chart's inset data points. The chart
                                    (navyDots design grid) maps real point i to pixel
                                    fraction (i + LEAD) / ((N-1) + 2*LEAD) — the short flat
                                    lead-in/out run the curve to the borders. We mirror that
                                    here. The current (latest) reading is boxed. */}
                                {(() => {
                                    const reals = trendData.filter((p) => !p.dummy && p.name);
                                    const n = reals.length;
                                    return (
                                        <div className="relative mt-2 h-7">
                                            {reals.map((point, i) => {
                                                const isCurrent = i === n - 1;
                                                const pct =
                                                    n > 1 ? ((i + DESIGN_LEAD) / (n - 1 + 2 * DESIGN_LEAD)) * 100 : 50;
                                                return (
                                                    <span
                                                        key={i}
                                                        style={{left: `${pct}%`}}
                                                        className={`absolute -translate-x-1/2 whitespace-nowrap ${
                                                            isCurrent
                                                                ? '-top-0.5 rounded-md border border-mld-panel-border bg-white px-2.5 py-0.5 text-xs text-mld-text-primary'
                                                                : 'top-0 text-xs text-gray-500'
                                                        }`}>
                                                        {point.name}
                                                    </span>
                                                );
                                            })}
                                        </div>
                                    );
                                })()}
                            </div>
                        )}
                    </div>
                </div>

                {/* Biomarker Insights — collapsible, defaults to expanded (re-opens
                when a test is selected). Top header shows the panel label; the
                inner card mirrors the design's two-column layout (description
                left, status-specific implications on the right). */}
                {testEducation && (
                    <div className="mt-4">
                        <button
                            type="button"
                            onClick={() => setInsightsOpen((open) => !open)}
                            aria-expanded={insightsOpen}
                            className="flex items-center gap-1.5 text-sm font-medium text-mld-text-primary mb-2 hover:text-mld-primary transition-colors">
                            <svg
                                className="w-4 h-4"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                                aria-hidden="true">
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                                />
                            </svg>
                            Biomarker Insights
                            {insightsOpen ? (
                                <Icon name="chevron-up" className="w-4 h-4" />
                            ) : (
                                <Icon name="chevron-down" className="w-4 h-4" />
                            )}
                        </button>
                        {insightsOpen && (
                            <div className="bg-white rounded-2xl border border-mld-panel-border shadow-sm overflow-hidden">
                                {/* Header row */}
                                <div className="flex items-center justify-between border-b border-gray-200 px-6 py-3">
                                    <h4 className="text-base font-semibold text-mld-text-primary">Your Lab Result</h4>
                                    <button
                                        type="button"
                                        onClick={handleFullReport}
                                        className="inline-flex items-center gap-1.5 text-sm font-medium text-mld-text-primary hover:text-mld-primary transition-colors">
                                        Full Report
                                        <svg
                                            className="w-3.5 h-3.5"
                                            fill="none"
                                            stroke="currentColor"
                                            viewBox="0 0 24 24"
                                            aria-hidden="true">
                                            <path
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                strokeWidth={2}
                                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                            />
                                        </svg>
                                    </button>
                                </div>
                                {/* Two-column body */}
                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 px-6 py-5">
                                    {/* Left: value + pill + description */}
                                    <div>
                                        <div className="flex items-center gap-3 mb-3">
                                            <div className="flex items-baseline gap-1">
                                                <span className="text-2xl font-normal text-mld-text-primary leading-none">
                                                    {(() => {
                                                        const num = parseFloat(latestBiomarker.result);
                                                        return Number.isNaN(num)
                                                            ? latestBiomarker.result
                                                            : num.toFixed(num % 1 === 0 ? 0 : 1);
                                                    })()}
                                                </span>
                                                {hasNumericValue && latestBiomarker.units && (
                                                    <span className="text-xs text-gray-500">
                                                        {latestBiomarker.units}
                                                    </span>
                                                )}
                                            </div>
                                            {latestBiomarker.status && latestBiomarker.status !== 'Unknown' && (
                                                <span
                                                    className={`inline-flex items-center rounded-md px-3 py-1.5 text-xs font-medium border ${
                                                        latestBiomarker.status === 'Normal' ||
                                                        latestBiomarker.status === 'Optimal'
                                                            ? 'bg-mld-primary/10 text-mld-status-normal border-mld-primary'
                                                            : latestBiomarker.status === 'Borderline' ||
                                                                latestBiomarker.status === 'Elevated'
                                                              ? 'bg-mld-status-borderline/20 text-amber-700 border-mld-status-borderline'
                                                              : 'bg-mld-status-high/15 text-mld-status-high border-mld-status-high'
                                                    }`}>
                                                    {latestBiomarker.displayLabel}
                                                </span>
                                            )}
                                        </div>
                                        <p className="text-sm leading-relaxed text-mld-text-primary">
                                            {testEducation.description}
                                        </p>
                                    </div>
                                    {/* Right: status-specific implications */}
                                    <div className="lg:border-l lg:border-gray-200 lg:pl-6">
                                        <h5 className="text-sm font-semibold text-mld-text-primary mb-3">
                                            Your {cleanBiomarkerName(latestBiomarker.testName)} Level Indicates
                                        </h5>
                                        <ul className="space-y-2 text-sm text-mld-text-primary">
                                            {(() => {
                                                const status = latestBiomarker.status;
                                                const levelInfo =
                                                    status === 'High' || status === 'Positive' || status === 'Toxic'
                                                        ? testEducation.highLevel
                                                        : status === 'Low' ||
                                                            status === 'Deficient' ||
                                                            status === 'Impaired'
                                                          ? testEducation.lowLevel
                                                          : testEducation.normalLevel;
                                                const points: string[] = [];
                                                if (levelInfo?.description) points.push(levelInfo.description);
                                                if (
                                                    levelInfo &&
                                                    'symptoms' in levelInfo &&
                                                    Array.isArray(levelInfo.symptoms)
                                                ) {
                                                    points.push(...levelInfo.symptoms.slice(0, 2));
                                                }
                                                // Fallback: use the general description so the
                                                // panel never reads as empty.
                                                if (points.length === 0 && testEducation.description) {
                                                    points.push(testEducation.description);
                                                }
                                                return points.slice(0, 3).map((p, i) => (
                                                    <li key={i} className="flex items-start gap-2">
                                                        <span
                                                            className="mt-1 inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center rounded-full text-mld-status-normal text-xs font-bold"
                                                            aria-hidden="true">
                                                            +
                                                        </span>
                                                        <span>{p}</span>
                                                    </li>
                                                ));
                                            })()}
                                        </ul>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </Card>
        </section>
    );
};

export default LabResults;
